import { useEffect, useRef, useState } from "react";
import moment from "moment";
import { Spin } from "antd";
import Paper from "components/general/PaperComponent";
import Text from "components/general/Text";
import UserInfo from "components/cards/UserInfo";
import ModalWrapper from "components/modals/ModalWrapper";
import Button from "components/general/Button";
import {
  useAssignPunchMutation,
  useGetShiftPunchesQuery,
} from "apis/services/timekeeper";
import { AllStaffShiftsProps } from "apis/types/shift";
import {
  BOUNDARY_LABELS,
  BOUNDARY_VALUE_BY_KEY,
  PunchBoundaryKey,
  RESOLUTION_STATUS_LABELS,
  ShiftBoundaryDetails,
} from "apis/types/timekeeper";
import usePermissions from "hooks/usePermission";
import showSuccessMsg from "utils/showSuccessMsg";
import getBackendError from "utils/getBackendError";
import formatTime from "utils/formatDateTime";
import COLORS from "constants/Colors";
import UnassignConfirm from "./UnassignConfirm";
import PunchRow from "./PunchRow";
import { BOUNDARY_KEYS, DATE_TIME_FORMAT, sourceLabel } from "./helpers";
import styles from "./styles.module.scss";

interface ViewPunchesModalProps {
  isVisible: boolean;
  setIsVisible: (state: boolean) => void;
  shift: AllStaffShiftsProps | null;
}

export default function ViewPunchesModal({
  isVisible,
  setIsVisible,
  shift,
}: ViewPunchesModalProps) {
  // `${punchId}-${boundary}` of the assignment in flight, so the spinner sits
  // on the button that was clicked rather than on the whole list.
  const [assigningKey, setAssigningKey] = useState<string | null>(null);
  const [boundaryToUnassign, setBoundaryToUnassign] =
    useState<PunchBoundaryKey | null>(null);

  const { hasPermissionTo } = usePermissions();
  // Reading the punches and moving them are separate rights, so a reviewer can
  // be given the window without being given the ability to reassign it.
  const canViewPunches = hasPermissionTo("view_punches");
  const canAssignPunches = hasPermissionTo("assign_punches");

  // One shift-scoped call answers for complete, missing and absent shifts
  // alike, and covers the whole retrieval window including overnight punches.
  const {
    data,
    isLoading,
    isFetching,
    error: fetchError,
  } = useGetShiftPunchesQuery(
    { shift_id: shift?.id as number },
    { skip: !isVisible || !shift?.id || !canViewPunches },
  );

  const [assignPunch, { error: assignError, reset: resetAssign }] =
    useAssignPunchMutation();

  // `resetAssign` is rebuilt the moment an assignment starts, so it is held in
  // a ref. As an effect dependency it cleared the in-flight key on submit,
  // which dropped the button spinner and reopened it to a second click.
  const resetAssignRef = useRef(resetAssign);
  useEffect(() => {
    resetAssignRef.current = resetAssign;
  }, [resetAssign]);

  useEffect(() => {
    setAssigningKey(null);
    setBoundaryToUnassign(null);
    resetAssignRef.current();
  }, [shift?.id, isVisible]);

  // The boundary records carry the manual and reused flags, and the punch rows
  // read them back through the assignments that point at each boundary.
  const details: Record<PunchBoundaryKey, ShiftBoundaryDetails | null> = {
    check_in: data?.check_in ?? null,
    check_out: data?.check_out ?? null,
  };

  const punches = data?.punches ?? [];
  const status = data?.resolution_status;
  // A complete shift has nothing left to close, so the "nothing available"
  // notice belongs only to the states that are still waiting on a boundary.
  const hasOpenBoundary = !!status && status !== "complete";
  const hasAvailablePunch = punches.some((punch) => punch.available_for?.length);

  const statusToneClass = () => {
    if (status === "complete") return styles.statusComplete;
    if (status === "absent") return styles.statusAbsent;
    return styles.statusMissing;
  };

  // Both sides are full datetimes, so this holds over midnight.
  const getWorkedHours = () => {
    const checkIn = details.check_in?.time;
    const checkOut = details.check_out?.time;
    if (!checkIn || !checkOut) return "-";
    const hours = moment(checkOut).diff(moment(checkIn), "hours", true);
    return `${Math.round(hours * 100) / 100} Hours`;
  };

  const onAssign = (punchId: number, boundary: PunchBoundaryKey) => {
    if (!shift?.id || assigningKey) return;
    setAssigningKey(`${punchId}-${boundary}`);
    resetAssignRef.current();
    assignPunch({
      shift_id: shift.id,
      punch_id: punchId,
      boundary: BOUNDARY_VALUE_BY_KEY[boundary],
    })
      .unwrap()
      .then(() => {
        showSuccessMsg({ msg: "Punch has been assigned successfully!" });
      })
      // The middleware toasts it, the inline message below keeps it on screen.
      .catch(() => {})
      // The mutation invalidates Timekeeper, so the refetch brings the new
      // boundaries and the recalculated availability with it.
      .finally(() => setAssigningKey(null));
  };

  const errorMessage =
    getBackendError(fetchError) || getBackendError(assignError);

  const renderBoundary = (key: PunchBoundaryKey) => {
    const boundary = details[key];
    const label = BOUNDARY_LABELS[key];
    // Missing describes the boundary, never the punch that could close it.
    const isMissing = !boundary?.time;
    const timeLabel = isMissing
      ? "Missing"
      : moment(boundary?.time).format(DATE_TIME_FORMAT);

    const stateLabels = [
      ...(boundary?.punch_id
        ? [`punch ${boundary.punch_id}, ${sourceLabel(boundary.source)}`]
        : []),
      ...(boundary?.manual ? ["manually assigned"] : []),
      ...(boundary?.reused ? ["shared punch"] : []),
    ];

    return (
      <div
        key={key}
        role="group"
        aria-label={`${label}: ${timeLabel}${
          stateLabels.length ? `, ${stateLabels.join(", ")}` : ""
        }`}
        className={`${styles.recordedRow} ${
          isMissing ? styles.missingBoundary : ""
        }`}
      >
        <Text className={styles.recordedTitle}>{label}</Text>
        <Text
          className={`${styles.recordedValue} ${
            isMissing ? styles.missingValue : ""
          }`}
        >
          {timeLabel}
        </Text>
        <Text className={styles.punchTime}>
          {boundary?.punch_id
            ? `Punch #${boundary.punch_id} · ${sourceLabel(boundary.source)}`
            : "No punch assigned"}
        </Text>

        <div className={styles.recordedFooter}>
          <div className={styles.badgeGroup}>
            {isMissing && (
              <span className={`${styles.badge} ${styles.missingBadge}`}>
                Missing
              </span>
            )}
            {!isMissing &&
              (boundary?.manual ? (
                <span className={`${styles.badge} ${styles.locked}`}>
                  Manual
                </span>
              ) : (
                <span className={`${styles.badge} ${styles.automatic}`}>
                  Auto matched
                </span>
              ))}
            {!!boundary?.reused && (
              <span className={`${styles.badge} ${styles.sharedBadge}`}>
                Shared Punch
              </span>
            )}
          </div>

          {/* Only a manually assigned boundary can be handed back again. */}
          {!!boundary?.manual && canAssignPunches && (
            <Button
              type="button"
              isOutline
              btnClassName={styles.unassignBtn}
              ariaLabel={`Unassign ${label}${
                boundary?.punch_id ? `, punch ${boundary.punch_id}` : ""
              }, ${timeLabel}`}
              onClick={() => setBoundaryToUnassign(key)}
            >
              {`Unassign ${label}`}
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderPunchNotice = () => {
    if (fetchError) {
      return (
        <Text className={styles.noticeText}>
          Punches could not be loaded for this shift.
        </Text>
      );
    }

    // The two empty states answer different questions and never share a line:
    // one says the window is empty, the other says nothing in it can be used.
    if (!punches.length) {
      return (
        <Text className={styles.noticeText}>
          No punches found in this shift&apos;s retrieval window
        </Text>
      );
    }

    if (hasOpenBoundary && !hasAvailablePunch) {
      return (
        <Text className={styles.noticeText}>
          No punches are currently available for assignment
        </Text>
      );
    }

    return null;
  };

  return (
    <ModalWrapper
      headerTitle="Punches"
      size="large"
      isVisible={isVisible}
      setIsVisible={setIsVisible}
      isFooterHidden
    >
      <div className={styles.container}>
        {!!status && (
          <div
            role="status"
            className={`${styles.statusBar} ${statusToneClass()}`}
          >
            <Text className={styles.statusLabel}>Shift Status</Text>
            <Text className={styles.statusValue}>
              {RESOLUTION_STATUS_LABELS[status] ?? status}
            </Text>
          </div>
        )}

        <Paper
          backgroundColor={COLORS.light}
          borderColor={COLORS.lightGreyBackground}
          paddingVertical={24}
          paddingHorizontal={28}
        >
          <UserInfo data={shift?.employee} isTableCol />
        </Paper>

        <div className={styles.gridRow}>
          <div className={styles.detailCard}>
            <Text className={styles.detailTitle}>Member ID</Text>
            <Text className={styles.detailValue}>
              {shift?.employee?.cfm_id ?? "-"}
            </Text>
          </div>
          <div className={styles.detailCard}>
            <Text className={styles.detailTitle}>Shift Name</Text>
            <Text className={styles.detailValue}>
              {shift?.shift_name ?? "-"}
            </Text>
          </div>
          <div className={styles.detailCard}>
            <Text className={styles.detailTitle}>Shift Time</Text>
            <Text className={styles.detailValue}>
              {shift?.start_time && shift?.end_time
                ? `${formatTime(shift.start_time)} - ${formatTime(shift.end_time)}`
                : "-"}
            </Text>
          </div>
          <div className={styles.detailCard}>
            <Text className={styles.detailTitle}>Worked Hours</Text>
            <Text className={styles.detailValue}>{getWorkedHours()}</Text>
          </div>
        </div>

        {!canViewPunches ? (
          <Text className={styles.noticeText}>
            You do not have permission to view the punches of this shift.
          </Text>
        ) : isLoading ? (
          <div className="d-flex my-4">
            <Spin className="m-auto" />
          </div>
        ) : (
          <>
            <div className={styles.recordedSection}>
              <div className={styles.sectionHeaderRow}>
                <Text className={styles.sectionHeader}>Recorded Boundaries</Text>
                {!!isFetching && <Spin size="small" />}
              </div>
              <div className={styles.recordedGrid}>
                {BOUNDARY_KEYS.map(renderBoundary)}
              </div>
            </div>

            <div className={styles.assignSection}>
              <Text className={styles.sectionHeader}>
                Punches In Retrieval Window
              </Text>

              <div className={styles.punchList}>
                {punches.map((punch) => (
                  <PunchRow
                    key={punch.id}
                    punch={punch}
                    shiftId={shift?.id}
                    details={details}
                    canAssignPunches={canAssignPunches}
                    assigningKey={assigningKey}
                    onAssign={onAssign}
                  />
                ))}
              </div>

              {renderPunchNotice()}

              {!canAssignPunches && !!punches.length && (
                <Text className={styles.noticeText}>
                  You do not have permission to assign or unassign punches.
                </Text>
              )}
            </div>
          </>
        )}

        {!!errorMessage && (
          <Text className={styles.errorText}>{errorMessage}</Text>
        )}
      </div>

      {!!boundaryToUnassign && (
        <UnassignConfirm
          isVisible={!!boundaryToUnassign}
          setIsVisible={() => setBoundaryToUnassign(null)}
          shift={shift}
          boundary={boundaryToUnassign}
          punchTime={details[boundaryToUnassign]?.time}
          punchId={details[boundaryToUnassign]?.punch_id}
        />
      )}
    </ModalWrapper>
  );
}
